/**
 * Consent-gated PostHog React provider. Mount high in the app tree (root
 * layout / app shell). Listens to the cookie-consent banner's
 * `cookie-consent-update` event and (re-)initializes or tears down PostHog
 * accordingly so we never capture without explicit user opt-in.
 *
 * Pageview tracking is intentionally NOT included here — every host app has
 * a different router (Next.js App Router, TanStack Router, etc.) so each
 * app provides its own pageview tracker as a child of this provider and
 * uses `capturePageview()` from `./capture.ts`.
 *
 * Pass-through behaviour: when `apiKey` is missing (CI / local dev without
 * analytics) the provider becomes a no-op `<>{children}</>` — no errors,
 * no events.
 */

import posthog from "posthog-js";
import { PostHogProvider as Provider } from "posthog-js/react";
import { useEffect, type ReactNode } from "react";

import { CONSENT_UPDATE_EVENT, getStoredConsent, type CookieConsent } from "../consent";
import { initPostHog, teardownPostHog, type PostHogConfig } from "./init";

interface PostHogProviderProps extends PostHogConfig {
  children: ReactNode;
  /** Bypass the cookie-consent gate. ONLY for internal apps where staff
   *  are bound by employment contract / written policy and the public
   *  ePrivacy banner doesn't apply (e.g. apps/admin). Defaults to `false`
   *  — every public surface MUST keep this off. */
  bypassConsent?: boolean;
  /** Logical name for this surface — registered as a global property so
   *  every event auto-includes `app: "portal" | "marketing" | "admin"`.
   *  Used to slice dashboards without URL heuristics. */
  appName?: "portal" | "marketing" | "admin";
  /** Environment label — registered globally so dashboards can exclude
   *  dev / preview events. Defaults to "prod" if not provided. */
  environment?: "dev" | "preview" | "prod";
}

/** Global props registered after init so every captured event auto-receives
 *  `app` + `environment` (and any future globals) without per-call site
 *  boilerplate. Idempotent — safe to call after re-init on consent change. */
function registerGlobals(props: { app?: string; environment?: string }) {
  if (typeof window === "undefined" || !window.__POSTHOG_INITIALIZED__) return;
  const out: Record<string, string> = {};
  if (props.app) out.app = props.app;
  if (props.environment) out.environment = props.environment;
  if (Object.keys(out).length === 0) return;
  try {
    posthog.register(out);
  } catch {
    // swallow — telemetry must not break product
  }
}

export function PostHogProvider({
  apiKey,
  apiHost,
  debug,
  recordingSampleRate,
  disableAutocapture,
  bypassConsent,
  appName,
  environment,
  children,
}: PostHogProviderProps) {
  useEffect(() => {
    if (!apiKey) return;

    const baseConfig = { apiKey, apiHost, debug, recordingSampleRate, disableAutocapture };

    if (bypassConsent) {
      // Internal-app path: skip consent + init unconditionally. The cookie
      // banner is intentionally NOT mounted in apps/admin, so there is no
      // user-driven opt-in event to listen for.
      initPostHog(baseConfig);
      registerGlobals({ app: appName, environment });
      return;
    }

    const applyConsent = (consent: CookieConsent | null) => {
      if (consent?.analytics) {
        initPostHog(baseConfig);
        registerGlobals({ app: appName, environment });
      } else {
        teardownPostHog();
      }
    };

    // Initial check on mount.
    applyConsent(getStoredConsent());

    // Subscribe to consent changes from the cookie banner.
    const onConsentUpdate = (e: Event) => {
      const detail = (e as CustomEvent<CookieConsent>).detail;
      applyConsent(detail);
    };
    window.addEventListener(CONSENT_UPDATE_EVENT, onConsentUpdate);
    return () => window.removeEventListener(CONSENT_UPDATE_EVENT, onConsentUpdate);
  }, [
    apiKey,
    apiHost,
    debug,
    recordingSampleRate,
    disableAutocapture,
    bypassConsent,
    appName,
    environment,
  ]);

  if (!apiKey) {
    return <>{children}</>;
  }

  return <Provider client={posthog}>{children}</Provider>;
}
